/* eslint-disable no-unreachable-loop */
import { FC, useEffect, useMemo, useState, useCallback, useRef, startTransition } from 'react';
import Header from '@/components/Header/Header';
import styles from './NationalProducts.module.css';
import TableCard from '@/components/Tables/TableCard/TableCard';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import {
  useNationalProducts,
  useUpdateNationalProductsIslock,
} from '@/services/nationalproducts/nationalproducts.service';
import { NationalProductsDetails } from '@/types/nationalProducts.types';
import { PriceLockSwitchCell, MandatoryLockSwitchCell, VisibilitySwitchCell } from './internal/SwitchCell';
import Button from '@/components/Button/Button';
import { TextShorterCell } from './internal/TextShorterCell';
import { NationalProductsSelection } from '@/types/nationalProductsSelection.types';
import { useTranslation } from 'react-i18next';
import { useNationalProductsSaveState } from '@/hooks/useSaveButtonState';
import { useDebouncedUpdate } from '@/hooks/useDebouncedUpdate';

const NationalProducts: FC = () => {
  const { t } = useTranslation();
  const { data: dataNationalProducts, isLoading, isError, refetch } = useNationalProducts(true);
  const [data, setData] = useState<NationalProductsDetails[]>([]);
  const [originalData, setOriginalData] = useState<NationalProductsDetails[]>([]);
  const { mutate: updateIsLockForNationalProduct, isPending: isSaving } = useUpdateNationalProductsIslock();

  // Performance optimizations
  const isInitialMount = useRef(true);

  // Direct state updater for immediate visual feedback (no throttling/debouncing)
  const setDataDirect = useCallback(
    (newData: NationalProductsDetails[] | ((prev: NationalProductsDetails[]) => NationalProductsDetails[])) => {
      setData(newData);
    },
    [],
  );

  // Debounced update for non-interactive updates only
  const debouncedUpdate = useDebouncedUpdate(setDataDirect, 50);

  // Immediate update function for switch toggles - deferred to not block visual feedback
  // The switch itself uses flushSync for instant visual, this updates parent state after
  const immediateUpdate = useCallback((rowIndex: number, columnId: string, value: string | boolean) => {
    // Use startTransition to defer this update so it doesn't block the switch's visual feedback
    // The switch has already updated visually via flushSync, so this can be deferred
    startTransition(() => {
      setData((previousRows) => {
        const nextRows = [...previousRows];
        const targetRow = nextRows[rowIndex];
        if (targetRow) {
          const updatedRow = { ...targetRow, [columnId]: value } as NationalProductsDetails;
          nextRows[rowIndex] = updatedRow;
        }
        return nextRows;
      });
    });
  }, []);

  // Enhanced update function that provides both debounced and immediate updates
  const updateData = useCallback(
    (rowIndex: number, columnId: string, value: string | boolean) => {
      immediateUpdate(rowIndex, columnId, value);
    },
    [immediateUpdate],
  ) as typeof immediateUpdate & { immediate: typeof immediateUpdate; flush: () => void };

  // Attach immediate and flush methods
  (updateData as any).immediate = immediateUpdate;
  (updateData as any).flush = useCallback(() => {
    const updateFn = debouncedUpdate as typeof debouncedUpdate & { flush?: () => void };
    if (updateFn.flush) {
      updateFn.flush();
    }
  }, [debouncedUpdate]);

  const isIdenticalData = useNationalProductsSaveState(originalData, data);

  // Memoized columns with stable references
  const columns = useMemo(
    () => [
      {
        id: 'isVisible',
        cell: VisibilitySwitchCell,
        header: t('nationalProducts.table.visibility'),
        accessorKey: 'isVisible' as const,
        meta: {
          disableFilter: true,
          tooltipText: t('nationalProducts.table.visibilityTooltip'),
        },
        enableSorting: true,
        size: 50,
      },
      {
        id: 'mandatoryProductLock',
        cell: MandatoryLockSwitchCell,
        header: t('nationalProducts.table.mandatory'),
        accessorKey: 'mandatoryProductLock' as const,
        meta: {
          disableFilter: true,
          tooltipText: t('nationalProducts.table.mandatoryTooltip'),
        },
        enableSorting: true,
        size: 50,
      },
      {
        id: 'priceLock',
        cell: PriceLockSwitchCell,
        header: t('nationalProducts.table.lockPrice'),
        accessorKey: 'priceLock' as const,
        meta: {
          disableFilter: true,
          tooltipText: t('nationalProducts.table.priceLockTooltip'),
        },
        enableSorting: true,
        size: 50,
      },
      {
        id: 'productName',
        accessorFn: (originalRow: NationalProductsDetails) => originalRow.productName || '-',
        header: t('nationalProducts.table.product'),
        enableSorting: true,
        size: 200,
      },
      {
        id: 'variantDescription',
        accessorFn: (originalRow: NationalProductsDetails) => originalRow.variantDescription || '-',
        cell: TextShorterCell,
        header: t('nationalProducts.table.variations'),
        enableSorting: true,
        size: 300,
      },
    ],
    [t],
  );

  if (isError) {
    showToast({
      label: t('error.fetchingBundles'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingBundles',
    });
  }

  // Use ref to track current data for comparison without causing re-renders
  const dataRef = useRef<NationalProductsDetails[]>([]);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Optimized data initialization - removed data from dependencies to prevent circular updates
  useEffect(() => {
    if (dataNationalProducts?.data && Array.isArray(dataNationalProducts.data)) {
      const newData = dataNationalProducts.data;

      // Only update if data actually changed (avoid unnecessary re-renders)
      if (isInitialMount.current) {
        // Show whatever the backend provides without any auto-turn-off logic
        setData(newData);
        setOriginalData(newData);
        isInitialMount.current = false;
        dataRef.current = newData;
      } else {
        const currentData = dataRef.current;
        // Use deep comparison to avoid updates when data hasn't actually changed
        const currentIds = new Set(currentData.map((item) => item.variantId));
        const newIds = new Set(newData.map((item) => item.variantId));

        // Check if IDs are different
        if (currentIds.size !== newIds.size || ![...newIds].every((id) => currentIds.has(id))) {
          startTransition(() => {
            setData(newData);
            dataRef.current = newData;
          });
        } else {
          // Check if any existing items have changed
          const hasChanges = newData.some((newItem) => {
            const currentItem = currentData.find((item) => item.variantId === newItem.variantId);
            if (!currentItem) return true;
            return (
              currentItem.priceLock !== newItem.priceLock ||
              currentItem.mandatoryProductLock !== newItem.mandatoryProductLock ||
              currentItem.isVisible !== newItem.isVisible ||
              currentItem.productName !== newItem.productName ||
              currentItem.variantDescription !== newItem.variantDescription
            );
          });

          if (hasChanges) {
            startTransition(() => {
              setData(newData);
              dataRef.current = newData;
            });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataNationalProducts?.data]);

  // Optimized save handler with better change detection
  const onButtonClick = useCallback(() => {
    if (isSaving) return; // Prevent double-clicks

    // Flush any pending debounced updates before saving
    if (updateData.flush) {
      updateData.flush();
    }

    // Use Map for O(1) lookup instead of O(n) find operations
    const currentDataMap = new Map(data.map((item) => [item.variantId, item]));
    const changedItems: NationalProductsSelection[] = [];

    // More efficient change detection
    for (const originalProduct of originalData) {
      const currentProduct = currentDataMap.get(originalProduct.variantId);
      if (!currentProduct) continue;

      // Direct boolean comparison without conversion
      const priceLockChanged = Boolean(originalProduct.priceLock) !== Boolean(currentProduct.priceLock);
      const mandatoryLockChanged =
        Boolean(originalProduct.mandatoryProductLock) !== Boolean(currentProduct.mandatoryProductLock);
      const isVisibleChanged = Boolean(originalProduct.isVisible) !== Boolean(currentProduct.isVisible);

      if (priceLockChanged || mandatoryLockChanged || isVisibleChanged) {
        changedItems.push({
          variantId: currentProduct.variantId,
          priceLock: Boolean(currentProduct.priceLock),
          mandatoryProductLock: Boolean(currentProduct.mandatoryProductLock),
          isVisible: Boolean(currentProduct.isVisible),
        });
      }
    }

    // Early return if no changes
    if (changedItems.length === 0) {
      showToast({
        id: 'noChangesToSave',
        label: 'No changes to save',
        toastType: ToastTypes.Info,
      });
      return;
    }

    // Optimistic update for better UX
    const currentDataSnapshot = [...data];

    updateIsLockForNationalProduct(changedItems, {
      onSuccess: () => {
        showToast({
          id: 'editNationalPrice',
          label: t('nationalProducts.isLockUpdated'),
        });
        // Update original data to reflect the saved state
        setOriginalData(currentDataSnapshot);
        // Refetch in background to ensure data consistency
        refetch();
      },
      onError: (error) => {
        console.error('Error updating national products:', error);
        showToast({
          id: 'errorUpdatingNationalProductsIsLock',
          label: t('error.updateNationalProduct'),
          toastType: ToastTypes.Error,
        });
        // Revert optimistic update on error
        setData(originalData);
      },
    });
  }, [data, originalData, updateData, updateIsLockForNationalProduct, t, refetch, isSaving]);

  // Stabilize updateData reference to prevent unnecessary re-renders of table cells
  const updateDataRef = useRef(updateData);
  useEffect(() => {
    updateDataRef.current = updateData;
  }, [updateData]);

  // Memoized table meta to prevent unnecessary re-renders
  // Use ref for updateData to maintain stable reference even when function changes
  const tableMeta = useMemo(
    () => ({
      columns,
      data,
      updateData: updateDataRef.current,
    }),
    [columns, data],
  );

  return (
    <>
      <Header title={t('nationalProducts.title')} breadcrumb={{ to: '/', label: t('breadcrumb.backToHome') }} />
      <div className={styles.page}>
        <div className={styles.tableLabel}>
          {t('nationalProducts.allProductsHeader')}
          <Button disabled={isIdenticalData || isSaving} onClick={onButtonClick} isLoading={isSaving}>
            {isSaving ? 'Saving...' : t('nationalProducts.saveButton')}
          </Button>
        </div>
        <TableCard
          height="calc(75vh - 100px)"
          isLoading={isLoading}
          data={data}
          meta={tableMeta}
          columns={columns}
          enableFilters
          itemIdentifier="variantId"
        />
      </div>
    </>
  );
};

export default NationalProducts;
